import "dayjs/locale/ja";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/types";
import { useGetCompanyQuery, useUpdateCompanyMutation } from "../../../services/company";
import { useState } from "react";
import { Button, Descriptions, Form, Input, Select, Skeleton, TimePicker } from "antd";
import { EditFilled, EnvironmentOutlined } from "@ant-design/icons";
import HrnModal from "../../../components/HrnModal";
import validateMessages, { requiredRule } from "../../../utils/validate";
import { useGetBusinessTypesQuery } from "../../../services/misc";
import { useGetDivisionsQuery, useLazyGetAreasQuery, useLazyGetDistrictsQuery, useLazyGetThanasQuery } from "../../../services/location";
import hrnUtils from "../../../utils/hrnUtils";
import { useGetServiceCentersQuery } from "../../../services/serviceCenter";
import ServiceMan from "../ServiceMan";
import LocationPicker from "../../../components/LocationPicker";

const CompanySettings = () => {
    const { loggedInUser } = useSelector((state: RootState) => state.global);
    const [companyForm] = Form.useForm();
    const [openInfoModal, setOpenInfoModal] = useState(false);
    const [districts, setDistricts] = useState<any[]>([]);
    const [thanas, setThanas] = useState<any[]>([]);
    const [areas, setAreas] = useState<any[]>([]);

    const [updateCompany, updateCompanyResult] = useUpdateCompanyMutation();
    const {
        data: businessTypes,
        isSuccess: isBTSuccess,
    } = useGetBusinessTypesQuery({});

    const {
        data: organization,
        isSuccess: isOrgSuccess,
        isFetching: isOrgFetching,
        isLoading: isOrgLoading
    } = useGetCompanyQuery({ id: loggedInUser?.currentCompany?.id });

    const {
        data: serviceCenters,
        isSuccess: isSCSuccess,
        isFetching: isSCFetching
    } = useGetServiceCentersQuery({ companyId: loggedInUser?.currentCompany?.id });

    const {
        data: divisions,
        isSuccess: isDivSuccess,
    } = useGetDivisionsQuery({});

    const [getDistricts, getDistrictsResult] = useLazyGetDistrictsQuery();
    const [getThanas, getThanasResult] = useLazyGetThanasQuery();
    const [getAreas, getAreasResult] = useLazyGetAreasQuery();

    const onDivisionChanged = async (divisionId: number) => {
        const districtList = await getDistricts({ divisionId }).unwrap();
        setDistricts(districtList);
    };
    const onDistrictChanged = async (districtId: number) => {
        const thanaList = await getThanas({ districtId }).unwrap();
        setThanas(thanaList);
    };
    const onThanaChanged = async (thanaId: number) => {
        const areaList = await getAreas({ thanaId }).unwrap();
        setAreas(areaList);
    };

    const showInfoModal = () => {
        onDivisionChanged(organization.divisionId);
        onDistrictChanged(organization.districtId);
        onThanaChanged(organization.thanaId);
        companyForm.setFieldsValue({ ...organization });
        setOpenInfoModal(true);
    };

    const hideInfoModal = () => {
        setOpenInfoModal(false);
        companyForm.resetFields();
    };

    const onSaveOrgInfo = async () => {
        try {
            await companyForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = companyForm.getFieldsValue();

        try {
            await updateCompany({ id: loggedInUser?.currentCompany?.id, body: formFields }).unwrap();
            setOpenInfoModal(false);
            companyForm.resetFields();
        } catch (e) { }
    };

    return (
        <>
            {(isOrgFetching || isOrgLoading) && <Skeleton key={1} />}

            {(isOrgSuccess && organization) &&
                <Descriptions title={<span>Organization Info</span>} size="small" extra={<Button type="link" onClick={showInfoModal}><EditFilled /></Button>}>
                    <Descriptions.Item label="Name">{organization?.name}</Descriptions.Item>
                    <Descriptions.Item label="Address Line1">{organization?.addressLine1 || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Address Line2">{organization?.addressLine2 || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Email">{organization?.email || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{organization?.phone || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Business Type">{organization?.businessType?.name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Division">{organization?.division?.name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="District">{organization?.district?.name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Thana">{organization?.thana?.name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Area">{organization?.area?.name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Location">{
                        organization?.location ? <a href={`https://www.google.com/maps?q=${organization.location}`} target="_blank" rel="noopener noreferrer">
                            <EnvironmentOutlined title="View on Google Maps" />
                        </a> : "-"
                    }</Descriptions.Item>
                </Descriptions>}
            <div>
                <HrnModal
                    open={openInfoModal}
                    title={"Edit information"}
                    width={520}
                    isLoading={updateCompanyResult.isLoading as boolean}
                    onCancel={hideInfoModal}
                    onSave={onSaveOrgInfo}
                >
                    <div className="mt-6">
                        <Form
                            form={companyForm}
                            layout="vertical"
                            name="companyForm"
                            validateMessages={validateMessages}
                        >
                            <Form.Item name="name" label={<>Name<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                <Input size="large" placeholder={"Name"} maxLength={30} />
                            </Form.Item>
                            <Form.Item name="addressLine1" label={"Address Line1"} rules={[requiredRule]}>
                                <Input size="large" placeholder={"Address Line1"} maxLength={200} />
                            </Form.Item>
                            <Form.Item name="addressLine2" label={"Address Line2"} >
                                <Input size="large" placeholder={"Address Line2"} maxLength={200} />
                            </Form.Item>
                            <Form.Item name="email" label={<>Email<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[{ required: true, type: "email", message: "Required" }]} >
                                <Input inputMode="email" size="large" placeholder="Email address" />
                            </Form.Item>
                            <Form.Item name="phone" label={<>Phone <span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                <Input inputMode="tel" size="large" placeholder="Phone" />
                            </Form.Item>
                            <Form.Item name="businessTypeId" label={<>Business Type <span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                <Select
                                    className="w-full"
                                    placeholder={"Business type"}
                                    options={
                                        (isBTSuccess ? businessTypes : [])?.map((x: any) => ({
                                            label: x.name,
                                            value: x.id,
                                        }))
                                    }
                                />
                            </Form.Item>
                            <Form.Item name="divisionId" label={<>Division</>}>
                                <Select
                                    className="w-full"
                                    placeholder={"Division"}
                                    options={
                                        (isDivSuccess ? divisions : [])?.map((x: any) => ({
                                            label: x.name,
                                            value: x.id,
                                        }))
                                    }
                                    allowClear
                                    showSearch
                                    filterOption={hrnUtils.dropdownFilterOption}
                                    onChange={onDivisionChanged}
                                />
                            </Form.Item>
                            <Form.Item name="districtId" label={<>District</>}>
                                <Select
                                    className="w-full"
                                    placeholder={"District"}
                                    options={
                                        (getDistrictsResult.isSuccess ? districts : [])?.map((x: any) => ({
                                            label: x.name,
                                            value: x.id,
                                        }))
                                    }
                                    allowClear
                                    showSearch
                                    filterOption={hrnUtils.dropdownFilterOption}
                                    onChange={onDistrictChanged}
                                />
                            </Form.Item>
                            <Form.Item name="thanaId" label={<>Thana</>}>
                                <Select
                                    className="w-full"
                                    placeholder={"Thana or upazila"}
                                    options={
                                        (getThanasResult.isSuccess ? thanas : [])?.map((x: any) => ({
                                            label: x.name,
                                            value: x.id,
                                        }))
                                    }
                                    allowClear
                                    showSearch
                                    filterOption={hrnUtils.dropdownFilterOption}
                                    onChange={onThanaChanged}
                                />
                            </Form.Item>
                            <Form.Item name="areaId" label={<>Area</>}>
                                <Select
                                    className="w-full"
                                    placeholder={"Area or village"}
                                    options={
                                        (getAreasResult.isSuccess ? areas : [])?.map((x: any) => ({
                                            label: x.name,
                                            value: x.id,
                                        }))
                                    }
                                    filterOption={hrnUtils.dropdownFilterOption}
                                    allowClear
                                    showSearch
                                />
                            </Form.Item>
                            <Form.Item name="location" label={<>Location</>}>
                                <LocationPicker />
                            </Form.Item>
                        </Form>
                    </div>
                </HrnModal>
            </div>
            {(loggedInUser?.currentCompany?.id && isSCSuccess) && <ServiceMan
                companyId={loggedInUser.currentCompany.id} />
            }
        </>
    );
};

export default CompanySettings;